import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchSurgeStats } from "../../service/api/adminApi";

const SurgeStatsPage = () => {
    const { id } = useParams();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const PER_PAGE = 15;

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchSurgeStats(id, { page: 1, per_page: PER_PAGE });
                setStats(res);
                setPagination(res.pagination);
                setPage(1);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const loadPage = useCallback(async (nextPage) => {
        setTableLoading(true);
        try {
            const res = await fetchSurgeStats(id, { page: nextPage, per_page: PER_PAGE });
            setStats(prev => ({ ...prev, orders: res.orders }));
            setPagination(res.pagination);
            setPage(nextPage);
        } catch (e) {
            console.error(e);
        } finally {
            setTableLoading(false);
        }
    }, [id]);

    const fmt = (n) => `₹${parseFloat(n || 0).toFixed(2)}`;
    const fmtDate = (d) =>
        new Date(d).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
        });

    const Pagination = () => {
        if (!pagination || pagination.last_page <= 1) return null;

        const { current_page, last_page, total, per_page } = pagination;
        const from = (current_page - 1) * per_page + 1;
        const to   = Math.min(current_page * per_page, total);

        const pages = [];
        const delta = 2;
        const left  = current_page - delta;
        const right = current_page + delta;

        for (let i = 1; i <= last_page; i++) {
            if (i === 1 || i === last_page || (i >= left && i <= right)) {
                pages.push(i);
            } else if (i === left - 1 || i === right + 1) {
                pages.push("…");
            }
        }

        return (
            <div className="surge-pagination">
                <span className="surge-pagination-info">
                    Showing {from}–{to} of {total} orders
                </span>
                <div className="surge-pagination-controls">
                    <button
                        onClick={() => loadPage(current_page - 1)}
                        disabled={current_page === 1 || tableLoading}
                        className="surge-page-btn"
                    >
                        ‹
                    </button>

                    {pages.map((p, i) =>
                        p === "…" ? (
                            <span key={`ellipsis-${i}`} className="surge-page-ellipsis">…</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => p !== current_page && loadPage(p)}
                                disabled={tableLoading}
                                className={`surge-page-btn${p === current_page ? " surge-page-btn--active" : ""}`}
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => loadPage(current_page + 1)}
                        disabled={current_page === last_page || tableLoading}
                        className="surge-page-btn"
                    >
                        ›
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="surge-page">
            <div className="surge-page-head">
                <div className="surge-topbar">
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexDirection: "column" }}>
                        <div className="surge-page-title_2">
                            {loading ? "Surge Details" : stats?.surge?.name ?? "Surge Details"}
                        </div>
                        {stats?.surge?.description && (
                            <div style={{ color: "#71717a", fontSize: "0.85rem" }}>
                                {stats.surge.description}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="surge-banner-skeleton">
                    <span className="surge-spinner" /> Loading stats…
                </div>
            ) : !stats ? (
                <div className="surge-empty-state">Failed to load stats.</div>
            ) : (
                <>
                    {/* ── Summary cards ── */}
                    <div className="surge-stats-grid">
                        <div className="surge-stat-card">
                            <div className="surge-stat-label">Completed Orders</div>
                            <div className="surge-stat-value">{stats.total_orders}</div>
                        </div>
                        <div className="surge-stat-card surge-stat-card--green">
                            <div className="surge-stat-label">Total Surge Collected</div>
                            <div className="surge-stat-value">{fmt(stats.total_surge_amount)}</div>
                        </div>
                        <div className="surge-stat-card surge-stat-card--blue">
                            <div className="surge-stat-label">Partner Payout Total</div>
                            <div className="surge-stat-value">{fmt(stats.partner_total)}</div>
                            <div className="surge-stat-sub">
                                Orders - {stats.total_orders} | Partner amount - {fmt(stats?.surge?.partner_amount)}
                            </div>
                        </div>
                        <div className="surge-stat-card surge-stat-card--amber">
                            <div className="surge-stat-label">Net Platform Surge</div>
                            <div className="surge-stat-value">
                                {fmt(stats.total_surge_amount - stats.partner_total)}
                            </div>
                            <div className="surge-stat-sub">Collected − Partner</div>
                        </div>
                    </div>

                    {/* ── Orders table ── */}
                    <div className="surge-section">
                        <div className="surge-section-header">
                            <div className="surge-section-title">Completed Orders</div>
                        </div>

                        {stats.orders.length === 0 ? (
                            <div className="surge-empty-state">No completed orders with this surge yet.</div>
                        ) : (
                            <>
                                <div className="surge-table-wrap" style={{ position: "relative" }}>
                                    {tableLoading && (
                                        <div className="surge-table-overlay">
                                            <span className="surge-spinner" />
                                        </div>
                                    )}
                                    <table className="surge-table">
                                        <thead>
                                        <tr>
                                            <th>Order ID</th>
                                            <th>Customer</th>
                                            <th>Order Total</th>
                                            <th>Surge Amount</th>
                                            <th>Partner Share</th>
                                            <th>Date</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {stats.orders.map(order => (
                                            <tr key={order.id}>
                                                <td className="surge-order-id">
                                                    <a
                                                        href={`/order/${order.unique_order_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: "#6366f1",
                                                            fontWeight: 700,
                                                            fontSize: "0.82rem",
                                                            textDecoration: "none",
                                                        }}
                                                        onMouseEnter={e => (e.target.style.textDecoration = "underline")}
                                                        onMouseLeave={e => (e.target.style.textDecoration = "none")}
                                                    >
                                                        {order.unique_order_id}
                                                    </a>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 500, fontSize: "0.82rem" }}>
                                                        {order.user?.name || "—"}
                                                    </div>
                                                </td>
                                                <td>{fmt(order.total)}</td>
                                                <td style={{ color: "#f59e0b", fontWeight: 600 }}>
                                                    {fmt(order.surge_amount)}
                                                </td>
                                                <td style={{ color: "#22c55e", fontWeight: 600 }}>
                                                    {fmt(order?.partner_surge_amount)}
                                                </td>
                                                <td style={{ color: "#71717a", fontSize: "0.78rem" }}>
                                                    {fmtDate(order.created_at)}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>

                                <Pagination />
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default SurgeStatsPage;